import type { FinanceTransaction, FinanceCategory } from '@/types/finance';
import type { DREPeriodFilter, DREMonthlyReport, DREMonthlySection, DREMonthlyLine } from './types';
import { distributeWithCharges } from './dreAllocations';

const SEM_CATEGORIA_ID = '__sem_categoria__';

function verticalPct(part: number, revenueTotal: number): number | null {
  if (revenueTotal === 0) return null;
  return (part / revenueTotal) * 100;
}

interface LineAcc {
  name: string;
  color: string;
  monthly: number[];
  total: number;
}

function buildSection(
  transactions: FinanceTransaction[],
  categoriesById: Map<string, FinanceCategory>,
  type: 'income' | 'expense',
  year: number,
  months: number[],
  revenueTotalRef: { value: number },
): DREMonthlySection {
  const monthIndex = new Map(months.map((m, i) => [m, i]));
  const linesMap = new Map<string, LineAcc>();

  const ensure = (key: string, name: string, color: string): LineAcc => {
    let acc = linesMap.get(key);
    if (!acc) {
      acc = { name, color, monthly: months.map(() => 0), total: 0 };
      linesMap.set(key, acc);
    }
    return acc;
  };

  for (const tx of transactions) {
    if (tx.type !== type || tx.status !== 'completed') continue;
    const refDate = tx.paymentDate ?? tx.dueDate;
    if (refDate.getFullYear() !== year) continue;
    const idx = monthIndex.get(refDate.getMonth() + 1);
    if (idx === undefined) continue;

    const allocations = distributeWithCharges(tx);
    if (allocations.length === 0) {
      const total = tx.amount + (tx.interest ?? 0) + (tx.fine ?? 0);
      const acc = ensure(SEM_CATEGORIA_ID, 'Sem categoria', '#94A3B8');
      acc.monthly[idx] = (acc.monthly[idx] ?? 0) + total;
      acc.total += total;
      continue;
    }
    for (const alloc of allocations) {
      const cat = categoriesById.get(alloc.categoryId);
      const key = cat?.id ?? SEM_CATEGORIA_ID;
      const acc = ensure(key, cat?.name ?? 'Sem categoria', cat?.color ?? '#94A3B8');
      acc.monthly[idx] = (acc.monthly[idx] ?? 0) + alloc.amount;
      acc.total += alloc.amount;
    }
  }

  const monthlyTotals = months.map((_, i) =>
    Array.from(linesMap.values()).reduce((s, l) => s + l.monthly[i]!, 0),
  );
  const total = Array.from(linesMap.values()).reduce((s, l) => s + l.total, 0);

  // Receita total vira a base da análise vertical de ambas as seções.
  if (type === 'income') revenueTotalRef.value = total;

  const lines: DREMonthlyLine[] = Array.from(linesMap.entries())
    .map(([id, l]) => ({
      categoryId: id === SEM_CATEGORIA_ID ? null : id,
      categoryName: l.name,
      color: l.color,
      monthly: l.monthly,
      total: l.total,
      verticalPct: verticalPct(l.total, revenueTotalRef.value),
    }))
    .filter((l) => l.total !== 0)
    .sort((a, b) => b.total - a.total);

  return {
    lines,
    monthlyTotals,
    total,
    verticalPct: verticalPct(total, revenueTotalRef.value),
  };
}

/**
 * Constrói a DRE evolutiva: categorias nas linhas, meses do período nas colunas,
 * com total e análise vertical (% sobre a receita total) por linha.
 *
 * Considera apenas lançamentos com status 'completed' (regime de caixa), usando
 * paymentDate quando disponível, senão dueDate. Splits e juros/multa são
 * distribuídos via distributeWithCharges (mesma regra das demais agregações).
 */
export function buildMonthlyDRE(
  transactions: FinanceTransaction[],
  categories: FinanceCategory[],
  filter: DREPeriodFilter,
): DREMonthlyReport {
  const fromMonth = Math.min(filter.fromMonth, filter.toMonth);
  const toMonth = Math.max(filter.fromMonth, filter.toMonth);
  const months: number[] = [];
  for (let m = fromMonth; m <= toMonth; m++) months.push(m);

  const categoriesById = new Map(categories.map((c) => [c.id, c]));
  const revenueTotalRef = { value: 0 };

  // income primeiro: fixa a base (receita total) usada na análise vertical de ambas.
  const income = buildSection(
    transactions,
    categoriesById,
    'income',
    filter.year,
    months,
    revenueTotalRef,
  );
  const expense = buildSection(
    transactions,
    categoriesById,
    'expense',
    filter.year,
    months,
    revenueTotalRef,
  );

  const netByMonth = months.map((_, i) => income.monthlyTotals[i]! - expense.monthlyTotals[i]!);
  const netTotal = income.total - expense.total;

  return {
    year: filter.year,
    fromMonth,
    toMonth,
    months,
    income,
    expense,
    netByMonth,
    netTotal,
    netVerticalPct: verticalPct(netTotal, revenueTotalRef.value),
  };
}
